<script lang="ts">
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import BatchApplyActionBox from "../../../batch-apply-action-box"
  import type {
    ContactOrderDigitalChannelChange,
    ContactOrderDigitalChannelChanges,
    ContactOrderDigitalPreset,
    ContactOrderPostalPreset,
  } from "../../_shared/_logic/preconfiguration"
  import type { ContactOrderDigitalChannel } from "../../../../_logic/contact-orders-contract"
  import {
    KEEP_PRECONFIGURATION_VALUE,
    preconfigurationSelectEventValue,
    withKeepPreconfigurationOption,
    type PreconfigurationChoice,
  } from "../../../../_logic/preconfiguration-keep"

  let {
    selectedCount,
    targetCount,
    pending,
    disabled,
    digitalPreset,
    digitalChannelChanges,
    postalPreset,
    onApply,
    onDigitalPresetChange,
    onDigitalChannelChange,
    onPostalPresetChange,
  }: {
    selectedCount: number
    targetCount: number
    pending: boolean
    disabled: boolean
    digitalPreset: PreconfigurationChoice<ContactOrderDigitalPreset>
    digitalChannelChanges: ContactOrderDigitalChannelChanges
    postalPreset: PreconfigurationChoice<ContactOrderPostalPreset>
    onApply: () => void | Promise<void>
    onDigitalPresetChange: (preset: PreconfigurationChoice<ContactOrderDigitalPreset>) => void
    onDigitalChannelChange: (
      channel: ContactOrderDigitalChannel,
      change: ContactOrderDigitalChannelChange,
    ) => void
    onPostalPresetChange: (preset: PreconfigurationChoice<ContactOrderPostalPreset>) => void
  } = $props()

  const digitalOptions = withKeepPreconfigurationOption([
    { value: "auto", label: "Kontaktwege automatisch auswählen" },
    { value: "custom", label: "Kontaktwege gezielt ändern …" },
    { value: "disabled", label: "Keine digitale Ankündigung" },
  ])
  const digitalChannels: { channel: ContactOrderDigitalChannel; label: string }[] = [
    { channel: "EMAIL", label: "E-Mail" },
    { channel: "MOBILE_MESSAGE", label: "Mobile Nachricht" },
    { channel: "PHONE", label: "Automatischer Anruf" },
  ]
  const channelOptions = withKeepPreconfigurationOption([
    { value: "enable", label: "Aktivieren" },
    { value: "disable", label: "Deaktivieren" },
  ])
  const postalOptions = withKeepPreconfigurationOption([
    { value: "priority_manual", label: "Bester Ansprechpartner · Manuell durch To-do" },
    { value: "priority_automatic", label: "Bester Ansprechpartner · Automatisch wenn fällig" },
    { value: "impersonal", label: "Unpersönlicher Brief · Manuell durch To-do" },
    { value: "notice", label: "Aushang · Selbst aushängen" },
    { value: "disabled", label: "Brief abwählen" },
  ])
</script>

<div class="form light preconfiguration-form">
  <BatchApplyActionBox
    label="Vorkonfiguration"
    {selectedCount}
    {targetCount}
    {pending}
    {disabled}
    {onApply}
  >
    <div class="row preconfiguration-fields">
      <Input
        type="select"
        placeholder="Digitale Ankündigung"
        value={digitalPreset}
        brightBackground={true}
        disabled={disabled || pending}
        options={digitalOptions}
        onChange={(event) => {
          const value = preconfigurationSelectEventValue(event)
          onDigitalPresetChange(
            value === "auto" || value === "disabled" || value === "custom"
              ? value
              : KEEP_PRECONFIGURATION_VALUE,
          )
        }}
      />
      <Input
        type="select"
        placeholder="Briefzustellung"
        value={postalPreset}
        brightBackground={true}
        disabled={disabled || pending}
        options={postalOptions}
        onChange={(event) => {
          const value = preconfigurationSelectEventValue(event)
          onPostalPresetChange(
            value === "priority_manual" ||
              value === "priority_automatic" ||
              value === "impersonal" ||
              value === "notice" ||
              value === "disabled"
              ? value
              : KEEP_PRECONFIGURATION_VALUE,
          )
        }}
      />
    </div>
    {#if digitalPreset === "custom"}
      <fieldset class="digital-channel-fields">
        <legend>Kontaktwege gezielt ändern</legend>
        <p>
          Aktivieren behält bereits gewählte Ansprechpartner bei. Ist der Kontaktweg noch aus,
          wird genau ein passender Ansprechpartner nach Priorität gewählt. Andere Kontaktwege und
          die Briefzustellung bleiben unverändert, sofern Sie diese nicht ebenfalls ändern.
        </p>
        <div class="channel-fields">
          {#each digitalChannels as { channel, label } (channel)}
            <Input
              type="select"
              placeholder={label}
              value={digitalChannelChanges[channel]}
              brightBackground={true}
              disabled={disabled || pending}
              options={channelOptions}
              onChange={(event) => {
                const value = preconfigurationSelectEventValue(event)
                onDigitalChannelChange(
                  channel,
                  value === "enable" || value === "disable" ? value : KEEP_PRECONFIGURATION_VALUE,
                )
              }}
            />
          {/each}
        </div>
      </fieldset>
    {/if}
  </BatchApplyActionBox>
</div>

<style lang="less">
  .preconfiguration-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  .digital-channel-fields {
    margin: 1rem 0 0;
    padding: 0;
    border: 0;

    legend {
      font-weight: 600;
    }

    p {
      margin: 0.5rem 0;
    }
  }

  .channel-fields {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.8rem 1rem;
  }

  @media (max-width: 48rem) {
    .preconfiguration-fields,
    .channel-fields {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
